// SEO helper: returns the current ISO 8601 date string for use as
// `dateModified` in JSON-LD schemas. Computed once per render so that
// every page automatically reflects the freshest possible date without
// us needing to maintain hardcoded strings.
//
// Returns YYYY-MM-DD (e.g. "2026-02-15"). Google parses both this and
// full timestamps; YYYY-MM-DD keeps the structured-data preview clean.
export const currentISODate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Returns { month, monthShort, year } for the current calendar date, in
// Australia/Adelaide time. Used by the homepage Adelaide Build Pulse so
// the "What we are seeing in <Month> <Year>" headline rolls forward each
// month automatically without manual edits.
export const currentMonthYear = () => {
  const now = new Date();
  // Format in Adelaide time so the rollover happens locally, not UTC.
  const fmt = (opts) =>
    new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Adelaide', ...opts }).format(now);
  return {
    month: fmt({ month: 'long' }),
    monthShort: fmt({ month: 'short' }),
    year: fmt({ year: 'numeric' }),
  };
};

// Build a BreadcrumbList JSON-LD schema given an ordered array of
// { name, url } items. The first item should be Home, the last item
// should be the current page. URL hosts default to canonical buildpilot.
export const buildBreadcrumbSchema = (items) => {
  if (!items || !items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((it, idx) => ({
      '@type': 'ListItem',
      'position': idx + 1,
      'name': it.name,
      'item': it.url.startsWith('http')
        ? it.url
        : `https://buildpilot.com.au${it.url}`,
    })),
  };
};

// LocalBusiness schema with geo coordinates and opening hours, matching
// the canonical BuildPilot business address. Optional `areaServed`
// override lets suburb/region pages narrow the business to that area.
export const buildLocalBusinessSchema = ({
  url,
  name = 'BuildPilot',
  description = "Australia's independent build CoPilot. Compare partner builders, designs and suburbs across Adelaide and Melbourne, with optional one-on-one support from a real human.",
  areaServed = [
    { '@type': 'Country', name: 'Australia' },
    { '@type': 'City', name: 'Adelaide' },
    { '@type': 'City', name: 'Melbourne' },
    { '@type': 'State', name: 'South Australia' },
    { '@type': 'State', name: 'Victoria' },
  ],
} = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': 'https://buildpilot.com.au/#localbusiness',
  name,
  description,
  url: url || 'https://buildpilot.com.au',
  telephone: '+61-1300-885-919',
  email: 'support@buildpilot.com.au',
  priceRange: '$$',
  image: 'https://buildpilot.com.au/images/buildpilot-logo-schema.png',
  logo: 'https://buildpilot.com.au/images/buildpilot-logo-schema.png',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Adelaide CBD',
    addressLocality: 'Adelaide',
    addressRegion: 'SA',
    postalCode: '5000',
    addressCountry: 'AU',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -34.9285,
    longitude: 138.6007,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:30',
      closes: '17:30',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday'],
      opens: '09:00',
      closes: '13:00',
    },
  ],
  areaServed,
  sameAs: [
    'https://www.facebook.com/buildpilot.com.au',
    'https://www.instagram.com/buildpilot.com.au',
    'https://www.linkedin.com/company/buildpilotau/',
  ],
});
